import {
  Box,
  Text,
  Spinner,
  VStack,
  HStack,
  NativeSelect,
  IconButton,
  Button,
} from '@chakra-ui/react';
import { FiPlus, FiX } from 'react-icons/fi'
import type { DashboardData, ChartPanelConfig, ChartType } from '../../types/analyticsMap'
import {
  IdeologyChart, PartyChart, RaceChart, AgeChart, TurnoutChart,
} from './charts/DistributionCharts'

interface AnalyticsDashboardProps {
  data: DashboardData | undefined
  isLoading: boolean
  chartPanels: ChartPanelConfig[]
  onChartPanelsChange: (panels: ChartPanelConfig[]) => void
}

// =====================================================================
// CHART TYPE REGISTRY
// =====================================================================

const CHART_TYPE_LABELS: Record<ChartType, string> = {
  ideology: 'Ideology Distribution',
  party: 'Party Breakdown',
  race: 'Race / Ethnicity',
  age: 'Age Distribution',
  turnout: 'Turnout Propensity',
}

const ALL_CHART_TYPES: ChartType[] = ['ideology', 'party', 'race', 'age', 'turnout']

const CHART_COMPONENTS: Record<ChartType, React.FC<{ data: DashboardData }>> = {
  ideology: IdeologyChart,
  party: PartyChart,
  race: RaceChart,
  age: AgeChart,
  turnout: TurnoutChart,
}

// =====================================================================
// MAIN DASHBOARD COMPONENT
// =====================================================================

export function AnalyticsDashboard({
  data,
  isLoading,
  chartPanels,
  onChartPanelsChange,
}: AnalyticsDashboardProps) {
  if (isLoading && !data) {
    return (
      <Box py={4} textAlign="center">
        <Spinner size="sm" color="accent" />
      </Box>
    )
  }

  if (!data) {
    return (
      <Box py={4}>
        <Text fontSize="xs" color="gray.400" textAlign="center">
          Move the map or add filters to see analytics
        </Text>
      </Box>
    )
  }

  const handleChangeType = (panelId: string, chartType: ChartType) => {
    onChartPanelsChange(
      chartPanels.map((p) => (p.id === panelId ? { ...p, chartType } : p))
    )
  }

  const handleRemovePanel = (panelId: string) => {
    if (chartPanels.length <= 1) return
    onChartPanelsChange(chartPanels.filter((p) => p.id !== panelId))
  }

  const handleAddPanel = () => {
    if (chartPanels.length >= ALL_CHART_TYPES.length) return
    const usedTypes = new Set(chartPanels.map((p) => p.chartType))
    const nextType = ALL_CHART_TYPES.find((t) => !usedTypes.has(t)) ?? 'ideology'
    onChartPanelsChange([
      ...chartPanels,
      { id: String(Date.now()), chartType: nextType },
    ])
  }

  return (
    <VStack gap={2} align="stretch">
      {/* Total contacts count */}
      <Box>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800">
          {data.totalContacts.toLocaleString()}
        </Text>
        <Text fontSize="xs" color="gray.500">contacts in view</Text>
      </Box>

      {/* Stackable chart panels */}
      <VStack gap={2} align="stretch" maxH="500px" overflowY="auto">
        {chartPanels.map((panel) => {
          const ChartComponent = CHART_COMPONENTS[panel.chartType]
          return (
            <Box
              key={panel.id}
              borderTop="1px"
              borderColor="gray.100"
              pt={2}
            >
              <HStack justify="space-between" mb={1}>
                <NativeSelect.Root size="xs">
                  <NativeSelect.Field
                    value={panel.chartType}
                    onChange={(e) => handleChangeType(panel.id, e.target.value as ChartType)}
                    w="200px"
                    fontWeight="medium">
                    {ALL_CHART_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {CHART_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <IconButton
                  aria-label="Remove chart"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleRemovePanel(panel.id)}
                  disabled={chartPanels.length <= 1}><FiX /></IconButton>
              </HStack>
              <ChartComponent data={data} />
            </Box>
          );
        })}
      </VStack>

      {/* Add panel button */}
      {chartPanels.length < ALL_CHART_TYPES.length && (
        <Button size="xs" variant="ghost" colorPalette="brand" onClick={handleAddPanel}><FiPlus />Add Chart
                  </Button>
      )}
    </VStack>
  );
}
